import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  OnboardingDraft,
  QuestionnaireResponse,
  QuestionnaireQuestion,
  LocalizedText,
} from '@gql/shared-types';
import { api, ApiError } from '../api/client.js';
import { Stepper } from '../components/Stepper.js';
import { FileUpload, type UploadedFile } from '../components/FileUpload.js';
import { baseLang } from '../i18n/index.js';

// Eigenständiger, einladungsbasierter Fall-Intake — GETRENNT von der
// allgemeinen Kundenregistrierung (/register → /onboarding). Enthält den
// fallspezifischen Fragebogen + Dokumenten-Upload + Vollmacht/Einwilligung.

type AnswerValue = string | boolean | string[];
type StepId = 'fragebogen' | 'uploads' | 'vollmacht' | 'review';

interface IntakePayload {
  fragebogen?: Record<string, AnswerValue> | undefined;
  vollmachtErteilt?: boolean | undefined;
}

function pickLabel(loc: LocalizedText | undefined, lang: string, fallback: string): string {
  if (!loc) return '';
  for (const l of [lang, fallback, 'de', 'en', 'ar', 'fr']) {
    const v = (loc as Record<string, string | undefined>)[l];
    if (typeof v === 'string' && v.trim().length > 0) return v;
  }
  return '';
}

function isAnswered(q: QuestionnaireQuestion, value: AnswerValue | undefined): boolean {
  if (q.type === 'heading') return true;
  if (value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'boolean') return value === true;
  if (Array.isArray(value)) return value.length > 0;
  return false;
}

export function CaseIntake() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = baseLang(i18n.language);

  const [stepIdx, setStepIdx] = useState(0);
  const [draft, setDraft] = useState<IntakePayload>({});
  const [uploads, setUploads] = useState<UploadedFile[]>([]);
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [data, q] = await Promise.all([api.getDraft(), api.getQuestionnaire()]);
        if (data) {
          setDraft({
            fragebogen: (data.draft?.fragebogen as Record<string, AnswerValue> | undefined) ?? {},
            vollmachtErteilt: data.draft?.vollmachtErteilt,
          });
          setUploads(data.uploads ?? []);
          if (data.submitted) setSubmitted(true);
        }
        setQuestionnaire(q);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          navigate('/login', { replace: true });
          return;
        }
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const saveDraft = useCallback((payload: IntakePayload) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.putDraft({ ...payload, uploads: [] } as OnboardingDraft)
        .catch((err) => console.error('[draft-save]', err));
    }, 600);
  }, []);

  const setAnswer = useCallback((questionId: string, value: AnswerValue) => {
    setDraft((prev) => {
      const next = { ...prev, fragebogen: { ...(prev.fragebogen ?? {}), [questionId]: value } };
      saveDraft(next);
      return next;
    });
  }, [saveDraft]);

  const setVollmacht = useCallback((v: boolean) => {
    setDraft((prev) => {
      const next = { ...prev, vollmachtErteilt: v };
      saveDraft(next);
      return next;
    });
  }, [saveDraft]);

  const handleUpload = async (files: File[], kategorie: string) => {
    setUploading(true);
    try {
      const res = await api.uploadFiles(files, kategorie);
      setUploads((prev) => [...prev, ...res]);
    } finally {
      setUploading(false);
    }
  };
  const handleRemove = async (id: string) => {
    await api.deleteUpload(id);
    setUploads((prev) => prev.filter((u) => u.id !== id));
  };

  const onSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await api.submitIntake({
        ...(draft.fragebogen ? { fragebogen: draft.fragebogen } : { fragebogen: {} }),
        vollmachtErteilt: true,
        uploads: uploads.map((u) => ({
          id: u.id, filename: u.filename, mimetype: u.mimetype, size: u.size,
          kategorie: u.kategorie as 'ausweis' | 'vollmacht' | 'korrespondenz' | 'beweis' | 'sonstiges',
        })),
        bestaetigungVollstaendig: true,
      });
      setSubmitted(true);
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.code === 'already_submitted' ? t('onboarding.errors.alreadySubmitted') : t('onboarding.errors.submitFailed'))
        : t('onboarding.errors.unexpected');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const steps = useMemo<StepId[]>(() => ['fragebogen', 'uploads', 'vollmacht', 'review'], []);
  const stepperItems = useMemo(
    () => steps.map((id) => ({ id, label: t(`onboarding.steps.${id}`) })),
    [steps, t],
  );

  if (loading) {
    return <div className="card"><h2 className="card-title">{t('common.loading')}</h2></div>;
  }

  if (submitted) {
    return (
      <div className="card">
        <h2 className="card-title">{t('onboarding.submittedTitle')}</h2>
        <div className="alert alert-success">{t('onboarding.submittedBody')}</div>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div className="card">
        <h2 className="card-title">{t('intake.noQuestionnaireTitle')}</h2>
        <p className="card-subtitle">{t('intake.noQuestionnaire')}</p>
        <Link to="/register" className="text-link">{t('notFound.toRegister')}</Link>
      </div>
    );
  }

  const currentId = steps[stepIdx];
  const answers = draft.fragebogen ?? {};
  const questionnaireTitle = pickLabel(questionnaire.title, lang, questionnaire.defaultLang);

  return (
    <div className="card card-wide">
      <h2 className="card-title">{questionnaireTitle || t('intake.title')}</h2>
      <p className="card-subtitle">{t('intake.subtitle')}</p>

      <Stepper steps={stepperItems} currentIndex={stepIdx} onStepClick={setStepIdx} />

      {currentId === 'fragebogen' && (
        <StepFragebogen questionnaire={questionnaire} answers={answers} setAnswer={setAnswer} lang={lang} />
      )}
      {currentId === 'uploads' && (
        <div>
          <p className="hint">{t('onboarding.uploads.hint')}</p>
          <FileUpload uploads={uploads} onUpload={handleUpload} onRemove={handleRemove} uploading={uploading} />
        </div>
      )}
      {currentId === 'vollmacht' && (
        <div>
          <p>{t('onboarding.vollmacht.p1')}</p>
          <p>{t('onboarding.vollmacht.p2')}</p>
          <div className="checkbox-field">
            <input id="voll" type="checkbox" checked={draft.vollmachtErteilt ?? false}
              onChange={(e) => setVollmacht(e.target.checked)} />
            <label htmlFor="voll">{t('onboarding.vollmacht.checkbox')}</label>
          </div>
        </div>
      )}
      {currentId === 'review' && (
        <StepReview
          questionnaire={questionnaire} answers={answers} uploads={uploads}
          vollmachtErteilt={draft.vollmachtErteilt ?? false} lang={lang}
          submitting={submitting} error={error} onSubmit={onSubmit}
        />
      )}

      <div className="wizard-actions">
        <button type="button" className="button button-secondary"
          onClick={() => setStepIdx((i) => Math.max(0, i - 1))} disabled={stepIdx === 0}>
          {t('common.back')}
        </button>
        {stepIdx < steps.length - 1 && (
          <button type="button" className="button"
            onClick={() => setStepIdx((i) => Math.min(steps.length - 1, i + 1))}
            disabled={!canAdvance(currentId, draft, questionnaire)}>
            {t('common.next')}
          </button>
        )}
      </div>
    </div>
  );
}

function canAdvance(step: StepId | undefined, d: IntakePayload, questionnaire: QuestionnaireResponse): boolean {
  if (step === 'fragebogen') {
    const answers = d.fragebogen ?? {};
    return questionnaire.questions
      .filter((q) => q.required && q.type !== 'heading')
      .every((q) => isAnswered(q, answers[q.id]));
  }
  if (step === 'vollmacht') return d.vollmachtErteilt === true;
  return true;
}

// --- Fragebogen-Rendering ---

function StepFragebogen({
  questionnaire, answers, setAnswer, lang,
}: {
  questionnaire: QuestionnaireResponse;
  answers: Record<string, AnswerValue>;
  setAnswer: (id: string, v: AnswerValue) => void;
  lang: string;
}) {
  const { t } = useTranslation();
  const total = questionnaire.questions.filter((q) => q.type !== 'heading').length;
  const answered = questionnaire.questions
    .filter((q) => q.type !== 'heading')
    .filter((q) => isAnswered(q, answers[q.id])).length;
  const desc = pickLabel(questionnaire.description, lang, questionnaire.defaultLang);
  return (
    <div className="questionnaire">
      <p className="hint">{desc || t('questionnaire.intro')}</p>
      <p className="field-counter">{t('questionnaire.answeredCount', { answered, total })}</p>
      {questionnaire.questions.map((q) => (
        <QuestionField key={q.id} q={q} value={answers[q.id]}
          onChange={(v) => setAnswer(q.id, v)} lang={lang} defaultLang={questionnaire.defaultLang} />
      ))}
    </div>
  );
}

function QuestionField({
  q, value, onChange, lang, defaultLang,
}: {
  q: QuestionnaireQuestion;
  value: AnswerValue | undefined;
  onChange: (v: AnswerValue) => void;
  lang: string;
  defaultLang: string;
}) {
  const { t } = useTranslation();
  const label = pickLabel(q.label, lang, defaultLang);
  const help = pickLabel(q.help, lang, defaultLang);
  if (q.type === 'heading') return <h3 className="review-h">{label}</h3>;
  const fieldId = `q_${q.id}`;
  const mark = q.required
    ? <span className="req-mark"> *</span>
    : <span className="optional-hint"> {t('questionnaire.optionalHint')}</span>;
  return (
    <div className="field">
      <label className="field-label" htmlFor={fieldId}>{label}{mark}</label>
      {help && <p className="hint">{help}</p>}
      {renderInput(q, fieldId, value, onChange, t, lang, defaultLang)}
    </div>
  );
}

function renderInput(
  q: QuestionnaireQuestion, fieldId: string, value: AnswerValue | undefined,
  onChange: (v: AnswerValue) => void, t: (k: string) => string, lang: string, defaultLang: string,
) {
  const labelOf = (loc: LocalizedText): string => pickLabel(loc, lang, defaultLang);
  const strVal = typeof value === 'string' ? value : '';
  switch (q.type) {
    case 'textarea':
      return <textarea id={fieldId} className="field-input field-textarea" rows={4} value={strVal}
        maxLength={20_000} onChange={(e) => onChange(e.target.value)} />;
    case 'date':
      return <input id={fieldId} type="date" className="field-input" value={strVal}
        onChange={(e) => onChange(e.target.value)} />;
    case 'number':
      return <input id={fieldId} type="number" className="field-input" inputMode="decimal" value={strVal}
        onChange={(e) => onChange(e.target.value)} />;
    case 'select':
      return (
        <select id={fieldId} className="field-input" value={strVal} onChange={(e) => onChange(e.target.value)}>
          <option value="">{t('questionnaire.selectPlaceholder')}</option>
          {(q.options ?? []).map((o) => <option key={o.value} value={o.value}>{labelOf(o.label)}</option>)}
        </select>
      );
    case 'radio':
      return (
        <div className="radio-group">
          {(q.options ?? []).map((o) => (
            <label key={o.value} className="radio-option">
              <input type="radio" name={fieldId} value={o.value}
                checked={strVal === o.value} onChange={() => onChange(o.value)} />
              <span>{labelOf(o.label)}</span>
            </label>
          ))}
        </div>
      );
    case 'checkbox': {
      if (q.options && q.options.length > 0) {
        const arr = Array.isArray(value) ? value : [];
        return (
          <div className="checkbox-group">
            {q.options.map((o) => (
              <label key={o.value} className="checkbox-option">
                <input type="checkbox" checked={arr.includes(o.value)}
                  onChange={(e) => onChange(e.target.checked ? [...arr, o.value] : arr.filter((x) => x !== o.value))} />
                <span>{labelOf(o.label)}</span>
              </label>
            ))}
          </div>
        );
      }
      return (
        <div className="checkbox-field">
          <input id={fieldId} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
          <label htmlFor={fieldId}>{t('common.yes')}</label>
        </div>
      );
    }
    case 'text':
    default:
      return <input id={fieldId} type="text" className="field-input" value={strVal} maxLength={2000}
        onChange={(e) => onChange(e.target.value)} />;
  }
}

function formatAnswer(q: QuestionnaireQuestion, value: AnswerValue | undefined, lang: string, fallback: string): string {
  if (value === undefined) return '';
  const optLabel = (v: string): string => {
    const o = (q.options ?? []).find((x) => x.value === v);
    return o ? pickLabel(o.label, lang, fallback) : v;
  };
  if (typeof value === 'boolean') return value ? 'Ja' : '';
  if (Array.isArray(value)) return value.map(optLabel).join(', ');
  if (q.type === 'select' || q.type === 'radio') return optLabel(value);
  return value;
}

function StepReview({
  questionnaire, answers, uploads, vollmachtErteilt, lang, submitting, error, onSubmit,
}: {
  questionnaire: QuestionnaireResponse;
  answers: Record<string, AnswerValue>;
  uploads: UploadedFile[];
  vollmachtErteilt: boolean;
  lang: string;
  submitting: boolean;
  error: string | null;
  onSubmit: () => void;
}) {
  const { t } = useTranslation();
  const requiredOk = questionnaire.questions
    .filter((q) => q.required && q.type !== 'heading')
    .every((q) => isAnswered(q, answers[q.id]));
  const ready = requiredOk && vollmachtErteilt;
  return (
    <div>
      <h3 className="review-h">{t('onboarding.review.fragebogen')}</h3>
      {error && <div className="alert alert-error">{error}</div>}
      <dl className="review">
        {questionnaire.questions.filter((q) => q.type !== 'heading').map((q) => (
          <div key={q.id} style={{ display: 'contents' }}>
            <dt>{pickLabel(q.label, lang, questionnaire.defaultLang)}</dt>
            <dd style={{ whiteSpace: 'pre-wrap' }}>
              {formatAnswer(q, answers[q.id], lang, questionnaire.defaultLang) || t('common.dash')}
            </dd>
          </div>
        ))}
        <dt>{t('onboarding.review.unterlagen', { count: uploads.length })}</dt>
        <dd>{uploads.length === 0 ? t('common.dash') : <ul>{uploads.map((u) => <li key={u.id}>{u.filename}</li>)}</ul>}</dd>
        <dt>{t('onboarding.review.vollmachtErklaerung')}</dt>
        <dd>{vollmachtErteilt ? t('common.yes') : t('common.dash')}</dd>
      </dl>
      <button className="button" disabled={!ready || submitting} onClick={onSubmit}>
        {submitting ? t('common.submitting') : t('common.submit')}
      </button>
      {!ready && <p className="field-error" style={{ marginTop: 8 }}>{t('intake.notReady')}</p>}
    </div>
  );
}
